import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import { useTableParams } from '../../core/hooks/table-params'
import { IColumn, Table } from '../../core/components/Table'
import { Pagination } from '../../core/components/Pagination'
import Loader from '../../core/components/Loader'
import { SimpleSearchPaginationParams } from '../../core/services/utils'
import { IAuthorProfile } from '../../core/interfaces'
import { getAllAuthorProfiles } from '../../services/blog-author-profiles'
import { BlogAuthorProfileModal } from './BlogAuthorProfileModal'

export const BlogAuthorProfileListPage: React.FC = (): JSX.Element => {
    const { page, setPage, size, setSize, totalElements, setTotalElements, sortBy, setSortBy } =
        useTableParams({ defaultSortBy: 'createdAt' })

    const [loading, setLoading] = useState(false)
    const [profiles, setProfiles] = useState<IAuthorProfile[]>([])
    const [isCheck, setIsCheck] = useState<string[]>([])
    const [showModal, setShowModal] = useState(false)
    const [editProfile, setEditProfile] = useState<IAuthorProfile | undefined>()
    const searchParams = useRef<{ [key: string]: string }>({})
    const timer = useRef<NodeJS.Timeout | null>(null)

    const getData = useCallback(async () => {
        setLoading(true)
        try {
            const params: SimpleSearchPaginationParams = { page, size, sortBy }
            params.text = searchParams.current.authorName
            const response = await getAllAuthorProfiles(params)
            setTotalElements(response.data.totalElements)
            setProfiles(response.data.content)
            setPage(page + 1)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [page, size, sortBy])

    useEffect(() => {
        getData()
    }, [getData, page, size, sortBy])

    const handleSortBy = (sort: string) => {
        setSortBy(sort === sortBy ? 'createdAt' : sort)
    }

    const handleSearchParams = useCallback(
        async (col: IColumn, q: string) => {
            if (timer.current) clearTimeout(timer.current)
            searchParams.current = { ...searchParams.current, [col.accessor]: q }
            timer.current = setTimeout(async () => {
                setLoading(true)
                try {
                    const params: SimpleSearchPaginationParams = { page: 0, size, sortBy }
                    params.text = searchParams.current.authorName
                    const { data, status } = await getAllAuthorProfiles(params)
                    if (status === 200) {
                        setProfiles(data.content)
                        setTotalElements(data.totalElements)
                        setSize(data.size)
                        setPage(data.pageNumber + 1)
                    }
                } catch (err) {
                    console.error(err)
                } finally {
                    setLoading(false)
                }
            }, 2000)
        },
        [page, size, sortBy],
    )

    const columns: IColumn[] = [
        {
            title: 'Avatar',
            accessor: '',
            render: (row) =>
                row.coverImageUrl ? (
                    <img
                        src={row.coverImageUrl}
                        alt={row.authorName}
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '50%' }}
                    />
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        { title: 'Name', accessor: 'authorName', sortable: true, searchable: true },
        { title: 'Subheading', accessor: 'subheading', sortable: false },
        {
            title: 'Linked User',
            accessor: '',
            render: (row) =>
                row.userId ? (
                    <span>
                        {row.userDisplayName}{' '}
                        <span className="text-muted">({row.userEmail})</span>
                    </span>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            title: 'Edit',
            accessor: '',
            render: (row) => (
                <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        setEditProfile(row)
                        setShowModal(true)
                    }}
                >
                    Edit
                </div>
            ),
        },
    ]

    return (
        <div style={{ padding: 20, backgroundColor: '#F8F8F8', position: 'relative' }}>
            {loading && <Loader />}
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 20,
                }}
            >
                <h2>Blog Author Profiles</h2>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                        setEditProfile(undefined)
                        setShowModal(true)
                    }}
                >
                    <i className="bi bi-plus-circle me-2 d-flex" />
                    New Author Profile
                </Button>
            </div>
            <div className="overflow-x-auto">
                <Table
                    handleSearch={handleSearchParams}
                    isCheck={isCheck}
                    columns={columns}
                    handleSortBy={handleSortBy}
                    data={profiles}
                />
            </div>
            <Pagination
                size={size}
                totalElements={totalElements}
                setSize={setSize}
                page={page + 1}
                changePage={(newPage) => setPage(newPage)}
            />
            <BlogAuthorProfileModal
                show={showModal}
                onHide={() => setShowModal(false)}
                editProfile={editProfile}
                onSaved={getData}
            />
        </div>
    )
}
